import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { closestCorners, DndContext, DragOverlay, MeasuringStrategy, pointerWithin, type CollisionDetection, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { serverTimestamp, writeBatch } from 'firebase/firestore'
import { FilterSheet } from '@/components/Filters'
import { InstallHint } from '@/components/InstallHint'
import { SelectionBar } from '@/components/SelectionBar'
import { loadDoneBetween, taskRef } from '@/data/tasks'
import { buzzOnPickUp, useDragSensors } from '@/hooks/useDragSensors'
import { useEventWindow, useShownEvents } from '@/hooks/useEvents'
import { usePaths } from '@/hooks/usePaths'
import { usePeople } from '@/hooks/usePeople'
import { useSwipe } from '@/hooks/useSwipe'
import { useTaskActions } from '@/hooks/useTaskActions'
import { useNow } from '@/hooks/useToday'
import { useVisibleTasks } from '@/hooks/useVisibleTasks'
import { useWide } from '@/hooks/useWide'
import { db } from '@/lib/firebase'
import { fire } from '@/lib/writeError'
import { addDays, isoWeekday, weekdayName } from '@/logic/dates'
import { eventsOnDay } from '@/logic/events'
import { needsRenumber, orderAt, renumber } from '@/logic/order'
import { doneRows, itemsOnDay, orderAtEnd, orderAtTop, shownDays, TRAY_ORDER, trayGroups, weekKind, type PlanItem } from '@/logic/plan'
import { usePrefs, type TrayKey } from '@/store/prefs'
import { useTasks } from '@/store/tasks'
import { setDefaultAddContext, setPlanWeek, showToast, stopSelecting, useUi } from '@/store/ui'
import type { DateStr, Task } from '@/types'
import { DayColumn, DaySection, DayStrip, type DayData } from './Day'
import { DragGhost, type CardContext } from './PlanCard'
import { PlanTopRow, PlanWeekRow } from './PlanHeader'
import { PlanSkeleton } from './PlanSkeleton'
import { Tray, TrayColumnHead, TrayStrip } from './Tray'
import './Plan.css'

/** A small tray count is easy to miss with corner distances: a target under the pointer wins. */
const collision: CollisionDetection = (args) => {
  const hits = pointerWithin(args)
  return hits.length ? hits : closestCorners(args)
}

/** A drawer group opening mid-drag moves the week down: the days' boxes are measured again as the card moves. */
const MEASURING = { droppable: { strategy: MeasuringStrategy.Always } }

/** One-offs ticked in a past week, read once: the listener only holds the last 30 days. */
function usePastWeek(week: DateStr, past: boolean): Task[] {
  const paths = usePaths()
  const [tasks, setTasks] = useState<Task[]>([])
  useEffect(() => {
    if (!past || !paths.hid) return
    let live = true
    loadDoneBetween(paths, week, addDays(week, 6))
      .then((t) => live && setTasks(t))
      .catch(() => live && setTasks([]))
    return () => {
      live = false
    }
  }, [week, past, paths.hid]) // eslint-disable-line react-hooks/exhaustive-deps
  return past ? tasks : NONE
}

const NONE: Task[] = []

/**
 * The Plan tab, the app's home: one week at a time, from today to Sunday in
 * the current week, with the tray for tasks with no day or a day that has
 * passed. Stacked folds days like a toggle list; Columns sets them side by
 * side. Past weeks open read-only as a record of what got done.
 */
export function PlanPage() {
  const wide = useWide()
  const now = useNow()
  const today = now.date
  const people = usePeople()
  const paths = usePaths()
  const actions = useTaskActions()
  const ready = useTasks((s) => s.ready)
  const week = useUi((s) => s.planWeek)
  const openEventDays = useUi((s) => s.openEventDays)
  const layout = usePrefs((s) => (wide ? s.layoutWide : s.layoutPhone))
  const eventsMode = usePrefs((s) => s.events)
  const hideDone = usePrefs((s) => !!s.filter.hideDone)
  const collapsedWeekdays = usePrefs((s) => s.collapsedWeekdays)
  const trayColumnOpen = usePrefs((s) => s.trayColumnOpen)
  const openGroups = usePrefs((s) => s.openGroups)
  const closeGroups = usePrefs((s) => s.closeGroups)
  const kind = weekKind(week, today)
  const past = kind === 'past'
  const pastTasks = usePastWeek(week, past)
  const tasks = useVisibleTasks(pastTasks)
  const shownEvents = useShownEvents()
  useEventWindow(week, addDays(week, 6))

  useEffect(() => setDefaultAddContext({}), [])
  // Select mode belongs to the Plan tab: leaving it ends the selection
  useEffect(() => stopSelecting, [])

  const ctx: CardContext = useMemo(() => ({ now, people, readOnly: past }), [now, people, past])
  const tray = useMemo(() => trayGroups(tasks, week, today, hideDone), [tasks, week, today, hideDone])
  // which groups have anything at all, before the filters: a filter narrows a group, never hides it or the tray
  const everyTask = useTasks((s) => s.all)
  const present = useMemo(() => {
    const all = trayGroups(everyTask, week, today, hideDone)
    return TRAY_ORDER.filter((k) => all[k].length > 0)
  }, [everyTask, week, today, hideDone])
  const days: DayData[] = useMemo(
    () =>
      shownDays(week, today).map((date) => {
        // a past week is the record of what was done, so it keeps its done rows whatever the filter
        const items = past ? doneRows(tasks, (d) => d === date) : itemsOnDay(tasks, date, today, hideDone)
        const events = past || eventsMode === 'off' ? [] : eventsOnDay(shownEvents, date)
        // with Count every day's events start closed on every visit; a tap opens one day
        return { date, items, events, eventsOpen: eventsMode === 'all' || openEventDays.includes(date), eventsFixed: eventsMode === 'all' }
      }),
    [week, today, past, tasks, eventsMode, shownEvents, openEventDays, hideDone],
  )

  // ---- dragging ----
  const sensors = useDragSensors()
  const [active, setActive] = useState<PlanItem | null>(null)
  const activeRef = useRef<PlanItem | null>(null)
  useEffect(() => {
    activeRef.current = active
  }, [active])
  const [overContainer, setOverContainer] = useState<string | null>(null)
  const spring = useRef<{ date: DateStr; timer: number } | null>(null)
  const openWeekday = usePrefs((s) => s.openWeekday)
  const openGroup = usePrefs((s) => s.openGroup)
  // the drawer's group a card from a day opened by being held over its button, folded again over the days
  const trayOpened = useRef<TrayKey | null>(null)

  const liveItems = useMemo(() => {
    const map = new Map<string, { item: PlanItem; container: string }>()
    for (const d of days) for (const item of d.items) if (!item.done && !item.faint) map.set(item.task.id, { item, container: `day:${d.date}` })
    for (const k of Object.keys(tray) as TrayKey[]) for (const item of tray[k]) if (!item.done && !item.faint && !map.has(item.task.id)) map.set(item.task.id, { item, container: `tray:${k}` })
    return map
  }, [days, tray])

  const clearSpring = () => {
    if (spring.current) window.clearTimeout(spring.current.timer)
    spring.current = null
  }

  const onDragStart = (e: DragStartEvent) => {
    buzzOnPickUp(e)
    setActive(liveItems.get(String(e.active.id))?.item ?? null)
  }

  // holding a card over a folded day for about a second springs it open, and it stays open
  const onDragOver = (e: DragOverEvent) => {
    const container = (e.over?.data.current?.container as string | undefined) ?? null
    setOverContainer(container)
    // in a phone's drawer, a card from a day held over Routines or No date opens that group, to be dropped among
    // its cards; back over the days the group folds again
    if (!wide && layout === 'stacked' && liveItems.get(String(e.active.id))?.container.startsWith('day:')) {
      if (container?.startsWith('tray:')) {
        const group = container.slice(5) as TrayKey
        if (trayOpened.current !== group && !openGroups.includes(group)) {
          openGroup(group, true)
          trayOpened.current = group
        }
      } else if (container?.startsWith('day:') && trayOpened.current) {
        closeGroups()
        trayOpened.current = null
      }
    }
    const date = container?.startsWith('day:') ? container.slice(4) : null
    if (!date || !collapsedWeekdays.includes(isoWeekday(date))) {
      clearSpring()
      return
    }
    if (spring.current?.date === date) return
    clearSpring()
    spring.current = { date, timer: window.setTimeout(() => openWeekday(isoWeekday(date)), 900) }
  }

  const onDragEnd = (e: DragEndEvent) => {
    clearSpring()
    // a group opened on the way stays open once the card is dropped in it
    trayOpened.current = null
    setActive(null)
    setOverContainer(null)
    const from = liveItems.get(String(e.active.id))
    const container = e.over?.data.current?.container as string | undefined
    if (!from || !container) return
    const task = from.item.task
    if (container.startsWith('tray:')) {
      const group = container.slice(5) as TrayKey
      if (container === from.container) {
        reorder(task, tray[group], String(e.over?.id))
        return
      }
      if (group !== 'routines' && group !== 'nodate') {
        showToast('Drop it on a day, Routines or No date')
        return
      }
      // among the group's cards it lands where dropped; on the count, where a new one would go
      const list = tray[group].filter((i) => !i.done && !i.faint).map((i) => i.task)
      const overAt = list.findIndex((t) => t.id === String(e.over?.id))
      const order = overAt >= 0 ? orderAt(list, overAt) : group === 'nodate' ? orderAtTop(tray.nodate) : orderAtEnd(tray.routines)
      if (actions.toTray(task, group, order) && overAt >= 0) renumberIfNeeded([...list.slice(0, overAt), { ...task, order }, ...list.slice(overAt)])
      return
    }
    const date = container.slice(4)
    const items = days.find((d) => d.date === date)?.items ?? []
    if (from.container === container) {
      reorder(task, items, String(e.over?.id))
      return
    }
    const list = items.filter((i) => !i.done && !i.faint).map((i) => i.task)
    const overTask = list.find((t) => t.id === String(e.over?.id))
    const at = overTask ? list.indexOf(overTask) : list.length
    const order = orderAt(list, at)
    actions.planOn(task, date, order)
    renumberIfNeeded([...list.slice(0, at), { ...task, order }, ...list.slice(at)])
  }

  /** A move within one list: the card lands where dnd-kit's preview showed it. */
  const reorder = (task: Task, items: PlanItem[], overId: string) => {
    const open = items.filter((i) => !i.done && !i.faint).map((i) => i.task)
    const oldIndex = open.findIndex((t) => t.id === task.id)
    if (oldIndex < 0) return
    const overIndex = open.findIndex((t) => t.id === overId)
    const newIndex = overIndex >= 0 ? overIndex : open.length - 1
    if (oldIndex === newIndex) return
    const moved = arrayMove(open, oldIndex, newIndex)
    const at = moved.findIndex((t) => t.id === task.id)
    const order = orderAt(moved.filter((t) => t.id !== task.id), at)
    actions.patch(task, { order })
    renumberIfNeeded(moved.map((t) => (t.id === task.id ? { ...t, order } : t)))
  }

  /** Only when the gaps between neighbours vanish is a day renumbered, in one batch. */
  const renumberIfNeeded = (sorted: Task[]) => {
    if (!needsRenumber(sorted)) return
    const batch = writeBatch(db)
    for (const { id, order } of renumber(sorted)) {
      const t = sorted.find((x) => x.id === id)!
      batch.update(taskRef(paths, t.scope, id), { order, updatedBy: paths.uid, updatedAt: serverTimestamp() })
    }
    fire(batch.commit())
  }

  // in Stacked a sideways swipe changes week; in Columns it scrolls the days
  const next = useCallback(() => setPlanWeek(addDays(useUi.getState().planWeek, 7)), [])
  const prev = useCallback(() => setPlanWeek(addDays(useUi.getState().planWeek, -7)), [])
  // a card being dragged owns the gesture
  const swipeRef = useSwipe(next, prev, { blocked: () => !!activeRef.current })

  if (!ready) return <PlanSkeleton />

  const anyTray = !past && present.length > 0
  const isCollapsed = (date: DateStr) => collapsedWeekdays.includes(isoWeekday(date))
  const overDay = (date: DateStr) => overContainer === `day:${date}`

  // a phone's Stacked layout: the top row and the tray are one light panel, pulled down over the week
  const drawer = !wide && layout === 'stacked'
  // An open drawer dims the week and puts it behind, like a sheet's backdrop: a tap there closes the drawer, and
  // the faded tasks are out of reach until it does. Picking a card up in the drawer lights the week and dims the
  // drawer until the drop; a card from a day held over the drawer dims the week, as an open drawer does.
  const drawerOpen = drawer && anyTray && present.some((k) => openGroups.includes(k))
  const fromDay = !!active && !!liveItems.get(active.task.id)?.container.startsWith('day:')
  const dim = !drawerOpen ? '' : !active || (fromDay && overContainer?.startsWith('tray:')) ? 'plan--dim-week' : 'plan--dim-tray'
  // never mid-drag: the card being carried came from the week
  const behind = dim === 'plan--dim-week' && !active

  const board = (() => {
    if (wide && layout === 'columns') {
      return (
        <div className="pboard">
          {anyTray &&
            (trayColumnOpen ? (
              <section className="tray-col" aria-label="Tray">
                <TrayColumnHead />
                <div className="tray-col__body">
                  <Tray tray={tray} present={present} ctx={ctx} single={false} flat overContainer={overContainer} />
                </div>
              </section>
            ) : (
              <TrayStrip tray={tray} present={present} />
            ))}
          {days.map((d) =>
            isCollapsed(d.date) ? (
              <DayStrip key={d.date} day={d} ctx={ctx} over={overDay(d.date)} />
            ) : (
              <DayColumn key={d.date} day={d} today={today} ctx={ctx} over={overDay(d.date)} short collapsible />
            ),
          )}
        </div>
      )
    }
    if (!wide && layout === 'columns') {
      return (
        <>
          <div className="pstrip" role="group" aria-label="Days">
            {days.map((d) => (
              <button key={d.date} type="button" className={`pstrip__day ${d.date === today ? 'pstrip__day--today' : ''}`} onClick={() => document.getElementById(`dcol-${d.date}`)?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })}>
                {weekdayName(d.date)}
                <b>{Number(d.date.slice(8))}</b>
              </button>
            ))}
          </div>
          {anyTray && (
            <div className="ptray-panel">
              <Tray tray={tray} present={present} ctx={ctx} single flat overContainer={overContainer} />
            </div>
          )}
          <div className="pcols">
            {days.map((d) => (
              <DayColumn key={d.date} day={d} today={today} ctx={ctx} over={overDay(d.date)} />
            ))}
          </div>
        </>
      )
    }
    return (
      <div className={`pstack ${wide ? 'pstack--wide' : ''}`} ref={swipeRef}>
        {/* on a phone the tray is the drawer under the top row, drawn with the header */}
        {anyTray && wide && <Tray tray={tray} present={present} ctx={ctx} single={false} flat={false} overContainer={overContainer} />}
        <div className="pstack__days" inert={behind}>
          {days.map((d) => (
            <DaySection key={d.date} day={d} today={today} ctx={ctx} over={overDay(d.date)} />
          ))}
        </div>
        {/* the open button in the drawer closes it too, so the backdrop needs no place in the tab order */}
        {behind && <div className="pstack__backdrop" aria-hidden="true" onClick={closeGroups} />}
      </div>
    )
  })()

  return (
    <div className={`plan plan--${layout} ${wide ? 'plan--wide' : 'plan--phone'} ${past ? 'plan--past' : ''} ${dim}`}>
      <DndContext sensors={sensors} collisionDetection={collision} measuring={MEASURING} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => {
        clearSpring()
        if (trayOpened.current) closeGroups()
        trayOpened.current = null
        setActive(null)
        setOverContainer(null)
      }}>
        {drawer ? (
          <div className="phead">
            <PlanTopRow week={week} today={today} />
            {anyTray && <Tray tray={tray} present={present} ctx={ctx} single flat={false} drawer overContainer={overContainer} />}
          </div>
        ) : wide ? (
          <PlanWeekRow week={week} today={today} />
        ) : (
          <PlanTopRow week={week} today={today} />
        )}
        {!wide && !past && <InstallHint />}
        {board}
        <DragOverlay dropAnimation={null}>{active ? <DragGhost item={active} ctx={ctx} /> : null}</DragOverlay>
      </DndContext>
      <FilterSheet />
      <SelectionBar />
    </div>
  )
}
